import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import KernteamLayout from '../../KernteamLayout';
import BackArrow from '../../BackArrow';
import { host_url } from '../../App';
import { CAMP_TAGE, aktuellerCampTag } from '../../campTage';
import ImportModal from '../ImportModal';
import "../logistik-base.css";
import "./Schichtplan.css";

const IMPORT_COLUMNS = [
  { key: 'Tag',          label: 'Tag' },
  { key: 'UhrzeitVon',   label: 'Von' },
  { key: 'UhrzeitBis',   label: 'Bis' },
  { key: 'JobName',      label: 'Dienst' },
  { key: 'Person',       label: 'Person' },
  { key: 'Regelmaessig', label: 'Regelmäßig' },
];

const MATRIX_STATIC_HEADERS = ['Tag', 'Von', 'Bis', 'Regelmäßig'];

const STANDARD_AUFGABEN = [
  {
    name: 'Stadttor',
    beschreibung: 'Wache',
    soll: '2 Personen',
  },
  {
    name: 'Nebentor',
    beschreibung: 'Wache',
    soll: '2 Personen',
  },
  {
    name: 'Kontrollgang',
    beschreibung: 'Toiletten, Papierhandtücher, WC-Mülleimer, Seife',
    soll: '2 Personen',
  },
  {
    name: 'Wasserstation',
    beschreibung: 'Aufräumen, Auffangschale leeren, DRK informieren',
    soll: '1 Person',
  },
  {
    name: 'Spüldienst',
    beschreibung: 'Einweiser und Helfer',
    soll: '2-3 Personen',
  },
  {
    name: 'Essensrückgabe',
    beschreibung: 'Rückgabestation betreuen, volle Behälter zum Spülmobil',
    soll: '2 Personen',
  },
  {
    name: 'Salatbar',
    beschreibung: 'Salatbar bewachen und Gemüse vorbereiten',
    soll: '1 Person',
  },
  {
    name: 'Zelte',
    beschreibung: 'Kinderstadt auf Spannung prüfen und zuschnüren',
    soll: 'Zelte',
  },
  {
    name: 'Selbstständigkeit',
    beschreibung: 'Selbstständige Kinder betreuen und begleiten',
    soll: '3 Personen',
  },
  {
    name: 'Verkehrsdienst',
    beschreibung: 'Verkehrsdienst und Einweisung',
    soll: '2 Personen',
  },
];

function normalizeDienst(dienst, index = 0) {
  const name = dienst.Name ?? dienst.name ?? '';
  const scopeKey = dienst.ScopeKey ?? dienst.scopeKey ?? 'ALL';
  const tag = dienst.Tag ?? dienst.tag ?? null;
  return {
    id: dienst.ID ?? dienst.id ?? null,
    name,
    originalName: dienst.originalName ?? name,
    tag: tag ? tagAlsString(tag) : null,
    scopeKey,
    beschreibung: dienst.Beschreibung ?? dienst.beschreibung ?? '',
    soll: dienst.Soll ?? dienst.soll ?? '',
    reihenfolge: dienst.Reihenfolge ?? dienst.reihenfolge ?? index + 1,
    aktiv: dienst.Aktiv !== false && dienst.Aktiv !== 0,
    custom: Boolean(dienst.custom),
  };
}

const SLOT_START = 7 * 60;
const SLOT_END = 23 * 60;
const SLOT_STEP = 15;

const ZEITRASTER = Array.from(
  { length: (SLOT_END - SLOT_START) / SLOT_STEP },
  (_, index) => SLOT_START + index * SLOT_STEP
);

function minutesToTime(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeToMinutes(value) {
  if (!value) return null;
  const text = String(value).substring(0, 5);
  const [hours, minutes] = text.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

function tagAlsString(tag) {
  if (!tag) return '';
  if (typeof tag === 'string') return tag.substring(0, 10);
  return new Date(tag).toISOString().substring(0, 10);
}

function wiederholungsGruppe(schicht) {
  return schicht?.WiederholungsGruppe ?? schicht?.wiederholungsGruppe ?? null;
}

function neueWiederholungsGruppe() {
  return `wiederholung-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function taskKey(name) {
  return String(name || '').trim().toLowerCase();
}

function schichtGiltFuerTag(schicht, selectedTag) {
  if (Boolean(schicht.Regelmaessig) && !wiederholungsGruppe(schicht)) return true;
  return tagAlsString(schicht.Tag) === selectedTag;
}

function schichtDecktSlot(schicht, slot) {
  const start = timeToMinutes(schicht.UhrzeitVon);
  if (start === null) return false;
  const end = schichtEndeMinuten(schicht);
  return start <= slot && slot < end;
}

function schichtEndeMinuten(schicht) {
  const start = timeToMinutes(schicht.UhrzeitVon);
  const rawEnd = timeToMinutes(schicht.UhrzeitBis);
  if (start === null) return rawEnd ?? SLOT_START + SLOT_STEP;
  return rawEnd === null || rawEnd <= start ? start + SLOT_STEP : rawEnd;
}

function schichtStartetInSlot(schicht, slot) {
  return timeToMinutes(schicht.UhrzeitVon) === slot;
}

function sortSchichten(a, b) {
  const startA = timeToMinutes(a.UhrzeitVon) ?? 0;
  const startB = timeToMinutes(b.UhrzeitVon) ?? 0;
  if (startA !== startB) return startA - startB;
  return String(a.Person || '').localeCompare(String(b.Person || ''), 'de');
}

function schichtZeitraum(schicht) {
  const von = schicht.UhrzeitVon ? schicht.UhrzeitVon.substring(0, 5) : '';
  const bis = schicht.UhrzeitBis ? schicht.UhrzeitBis.substring(0, 5) : '';
  if (von && bis && von !== bis) return `${von}-${bis}`;
  return von || bis || '';
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function htmlMitZeilenumbruechen(value) {
  return escapeHtml(value).replace(/\n/g, '<br>');
}

function parsePersonen(text) {
  return String(text || '')
    .split(/\r?\n|;|,/)
    .map(person => person.trim())
    .filter(Boolean);
}

function istWahr(value) {
  return ['ja', 'yes', '1', 'true', 'wahr', 'x'].includes(String(value || '').trim().toLowerCase());
}

function normalisiereDatum(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const deutsch = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
  if (deutsch) {
    const year = deutsch[3].length === 2 ? `20${deutsch[3]}` : deutsch[3];
    return `${year}-${deutsch[2].padStart(2, '0')}-${deutsch[1].padStart(2, '0')}`;
  }
  const slash = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slash) {
    const year = slash[3].length === 2 ? `20${slash[3]}` : slash[3];
    return `${year}-${slash[1].padStart(2, '0')}-${slash[2].padStart(2, '0')}`;
  }
  return text.substring(0, 10);
}

function normalisiereZeit(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const match = text.match(/(\d{1,2}):(\d{2})/);
  if (!match) return text;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

function headerKey(value) {
  return String(value || '').trim().toLowerCase().replace(/ä/g, 'ae').replace(/ß/g, 'ss');
}

function parseDienstHeader(value, reihenfolge) {
  const lines = String(value || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;
  return {
    Name: lines[0],
    Soll: lines[1] || null,
    Beschreibung: lines.slice(2).join(' ') || null,
    Reihenfolge: reihenfolge,
  };
}

function baueSchichtplanVorlage(XLSX, aufgaben) {
  const dienstNamen = aufgaben
    .map(aufgabe => aufgabe.name)
    .filter(name => name && taskKey(name) !== 'klo');
  const header = [...MATRIX_STATIC_HEADERS, ...dienstNamen];
  const beispielDefinitionen = [
    {
      Tag: '2026-07-06',
      UhrzeitVon: '08:00',
      UhrzeitBis: '09:00',
      Regelmaessig: 'NEIN',
      Dienste: { Stadttor: 'Max Müller\nAnna Bäcker' },
    },
    {
      Tag: '2026-07-06',
      UhrzeitVon: '09:00',
      UhrzeitBis: '10:00',
      Regelmaessig: 'NEIN',
      Dienste: { Nebentor: 'Marinus', Kontrollgang: 'Nils' },
    },
    {
      Tag: '2026-07-06',
      UhrzeitVon: '12:00',
      UhrzeitBis: '13:30',
      Regelmaessig: 'NEIN',
      Dienste: { Spüldienst: 'Thorsten', Essensrückgabe: 'Nils', Salatbar: 'Anna' },
    },
    {
      Tag: '2026-07-07',
      UhrzeitVon: '07:00',
      UhrzeitBis: '08:00',
      Regelmaessig: 'JA',
      Dienste: { Wasserstation: 'Ömer Yılmaz' },
    },
  ];
  const beispiel = beispielDefinitionen.map(row => ([
    row.Tag,
    row.UhrzeitVon,
    row.UhrzeitBis,
    row.Regelmaessig,
    ...dienstNamen.map(name => row.Dienste[name] || ''),
  ]));
  const ws = XLSX.utils.aoa_to_sheet([header, ...beispiel]);
  ws['!cols'] = header.map((_, index) => ({ wch: index < 4 ? 14 : 20 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Schichtplan');

  const hinweise = [
    ['HINWEISE ZUR SCHICHTPLAN-VORLAGE'],
    [''],
    ['Die ersten vier Spalten bleiben: Tag, Von, Bis, Regelmäßig.'],
    ['Alle weiteren Spalten sind Dienste. Wenn ein Dienst in der Kopfzeile noch nicht existiert, wird er beim Import angelegt.'],
    ['Mehrere Personen in einer Dienst-Zelle bitte untereinander schreiben. Semikolon oder Komma geht ebenfalls.'],
    ['Tag: JJJJ-MM-TT, zum Beispiel 2026-07-06.'],
    ['Von/Bis: HH:MM, zum Beispiel 08:00.'],
    ['Regelmäßig: JA oder NEIN.'],
  ];
  const wsHinweise = XLSX.utils.aoa_to_sheet(hinweise);
  wsHinweise['!cols'] = [{ wch: 100 }];
  XLSX.utils.book_append_sheet(wb, wsHinweise, 'Hinweise');
  return wb;
}

function parseSchichtplanMatrix(wb, XLSX) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false });
  const fehler = [];
  if (aoa.length === 0) return { rows: [], fehler: ['Die Datei enthält keine Daten.'], meta: { dienste: [] }, columns: IMPORT_COLUMNS };

  const header = aoa[0].map(cell => String(cell || '').trim());
  const keys = header.map(headerKey);
  const tagIndex = keys.indexOf('tag');
  const vonIndex = keys.indexOf('von');
  const bisIndex = keys.indexOf('bis');
  const regelIndex = keys.findIndex(key => key === 'regelmaessig' || key === 'regelmassig');

  if (tagIndex === -1 || vonIndex === -1 || bisIndex === -1) {
    return {
      rows: [],
      fehler: ['Die Kopfzeile muss mindestens Tag, Von und Bis enthalten.'],
      meta: { dienste: [] },
      columns: IMPORT_COLUMNS,
    };
  }

  const staticIndices = new Set([tagIndex, vonIndex, bisIndex, regelIndex].filter(index => index >= 0));
  const dienstSpalten = header
    .map((cell, index) => ({ index, dienst: parseDienstHeader(cell, index + 1) }))
    .filter(({ index, dienst }) => !staticIndices.has(index) && dienst?.Name);

  const dienstMap = new Map();
  dienstSpalten.forEach(({ dienst }, index) => {
    const key = taskKey(dienst.Name);
    if (!dienstMap.has(key)) {
      dienstMap.set(key, { ...dienst, Reihenfolge: index + 1 });
    }
  });

  const rows = [];
  aoa.slice(1).forEach((rawRow, rawIndex) => {
    const zeile = rawIndex + 2;
    const istLeer = rawRow.every(cell => !String(cell || '').trim());
    if (istLeer) return;

    const tag = normalisiereDatum(rawRow[tagIndex]);
    const von = normalisiereZeit(rawRow[vonIndex]);
    const bis = normalisiereZeit(rawRow[bisIndex]);
    const regelmaessig = regelIndex >= 0 ? istWahr(rawRow[regelIndex]) : false;

    if (!tag || !von || !bis) {
      fehler.push(`Zeile ${zeile}: Tag, Von und Bis müssen gefüllt sein.`);
      return;
    }

    dienstSpalten.forEach(({ index, dienst }) => {
      const personen = parsePersonen(rawRow[index]);
      personen.forEach(person => {
        rows.push({
          Tag: tag,
          JobName: dienst.Name,
          Person: person,
          UhrzeitVon: von,
          UhrzeitBis: bis,
          Regelmaessig: regelmaessig,
        });
      });
    });
  });

  return {
    rows,
    fehler,
    meta: { dienste: Array.from(dienstMap.values()) },
    columns: IMPORT_COLUMNS,
  };
}

function Schichtplan() {
  const [selectedTag, setSelectedTag] = useState(() => aktuellerCampTag());
  const [alleSchichten, setAlleSchichten] = useState([]);
  const [dienste, setDienste] = useState([]);
  const [diensteGeladen, setDiensteGeladen] = useState(false);
  const [dienstEditMode, setDienstEditMode] = useState(false);
  const [dienstDrafts, setDienstDrafts] = useState([]);
  const [deleteDienstConfirm, setDeleteDienstConfirm] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editBis, setEditBis] = useState('');
  const [editWiederholungsTage, setEditWiederholungsTage] = useState([]);
  const [resizingCell, setResizingCell] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const resizingRef = useRef(null);

  const ladeSchichten = useCallback(() => {
    fetch(`${host_url}/logistik/schicht`)
      .then(r => r.json())
      .then(data => setAlleSchichten(Array.isArray(data) ? data : []))
      .catch(err => console.error(err));
  }, []);

  const ladeDienste = useCallback((tag = selectedTag) => {
    const query = tag ? `?tag=${encodeURIComponent(tag)}` : '';
    setDiensteGeladen(false);
    fetch(`${host_url}/logistik/dienste${query}`)
      .then(r => r.json())
      .then(data => {
        setDienste(Array.isArray(data) ? data.map(normalizeDienst) : []);
        setDiensteGeladen(true);
      })
      .catch(err => {
        console.error(err);
        setDienste(STANDARD_AUFGABEN.map(normalizeDienst));
        setDiensteGeladen(false);
      });
  }, [selectedTag]);

  useEffect(() => {
    ladeDienste(selectedTag);
    ladeSchichten();
  }, [ladeDienste, ladeSchichten, selectedTag]);

  const aufgaben = useMemo(() => {
    const basisDienste = diensteGeladen ? dienste : STANDARD_AUFGABEN.map(normalizeDienst);
    const map = new Map(basisDienste.map((dienst, index) => [
      taskKey(dienst.name),
      { ...dienst, reihenfolge: dienst.reihenfolge || index + 1 },
    ]));
    if (!diensteGeladen) {
      alleSchichten
        .filter(schicht => schichtGiltFuerTag(schicht, selectedTag))
        .forEach(schicht => {
          const key = taskKey(schicht.JobName);
          if (key && !map.has(key)) {
            map.set(key, {
              id: null,
              name: schicht.JobName,
              originalName: schicht.JobName,
              beschreibung: '',
              soll: '',
              reihenfolge: map.size + 1,
              custom: true,
            });
          }
        });
    }
    return Array.from(map.values()).sort((a, b) => (a.reihenfolge || 999) - (b.reihenfolge || 999));
  }, [alleSchichten, dienste, diensteGeladen, selectedTag]);

  const matrixAufgaben = useMemo(() => {
    if (!dienstEditMode) return aufgaben;
    return dienstDrafts
      .map((dienst, index) => ({ ...dienst, draftIndex: index }))
      .filter(dienst => !dienst.geloescht)
      .map((dienst, index) => ({
        ...dienst,
        name: dienst.name || 'Neuer Dienst',
        matchName: dienst.originalName || dienst.name,
        reihenfolge: index + 1,
      }));
  }, [aufgaben, dienstDrafts, dienstEditMode]);

  const schichtenFuerTag = useMemo(
    () => alleSchichten.filter(schicht => schichtGiltFuerTag(schicht, selectedTag)),
    [alleSchichten, selectedTag]
  );

  const tagLabel = CAMP_TAGE.find(t => t.datum === selectedTag)?.label || selectedTag;

  function getCellEntries(aufgabe, slot) {
    const key = taskKey(aufgabe.matchName || aufgabe.name);
    return schichtenFuerTag
      .filter(schicht => taskKey(schicht.JobName) === key && schichtDecktSlot(schicht, slot))
      .sort(sortSchichten);
  }

  function getPrintCell(aufgabe, slot) {
    const entries = getCellEntries(aufgabe, slot);
    const startingEntries = entries.filter(entry => schichtStartetInSlot(entry, slot));
    if (entries.length > 0 && startingEntries.length === 0) return null;
    if (startingEntries.length === 0) return { entries: [], rowSpan: 1 };

    const maxEnd = Math.max(...startingEntries.map(schichtEndeMinuten));
    const rowSpan = Math.max(1, Math.ceil((maxEnd - slot) / SLOT_STEP));
    return { entries: startingEntries, rowSpan };
  }

  function beginEdit(aufgabe, slot, entries) {
    if (dienstEditMode) return;
    const firstEntry = entries[0] || null;
    const firstStart = timeToMinutes(firstEntry?.UhrzeitVon);
    const firstEnd = timeToMinutes(firstEntry?.UhrzeitBis);
    const gruppe = wiederholungsGruppe(firstEntry);
    const gruppenEintraege = gruppe
      ? alleSchichten.filter(schicht => wiederholungsGruppe(schicht) === gruppe)
      : [];
    const wiederholungsTage = gruppe
      ? [...new Set(gruppenEintraege.map(schicht => tagAlsString(schicht.Tag)).filter(Boolean))]
      : firstEntry?.Regelmaessig
        ? CAMP_TAGE.map(tag => tag.datum)
        : [selectedTag];
    setEditingCell({
      aufgabe: aufgabe.name,
      slot: firstStart !== null ? firstStart : slot,
      displaySlot: slot,
      entryId: firstEntry?.ID || null,
      wiederholungsGruppe: gruppe,
      alteIds: (gruppe ? gruppenEintraege : entries).map(entry => entry.ID).filter(Boolean),
    });
    setEditValue(entries.map(e => e.Person).filter(Boolean).join('\n'));
    setEditBis(firstEnd !== null && firstEnd > (firstStart ?? slot) ? firstEntry.UhrzeitBis.substring(0, 5) : minutesToTime(slot + SLOT_STEP));
    setEditWiederholungsTage(CAMP_TAGE
      .map(tag => tag.datum)
      .filter(tag => tag === selectedTag || wiederholungsTage.includes(tag)));
  }

  function cancelEdit() {
    setEditingCell(null);
    setEditValue('');
    setEditBis('');
    setEditWiederholungsTage([]);
  }

  function toggleWiederholungsTag(tag) {
    setEditWiederholungsTage(tage => {
      const next = new Set(tage.length > 0 ? tage : [selectedTag]);
      if (tag !== selectedTag) {
        if (next.has(tag)) next.delete(tag);
        else next.add(tag);
      }
      next.add(selectedTag);
      return CAMP_TAGE.map(campTag => campTag.datum).filter(datum => next.has(datum));
    });
  }

  async function saveCell(entries) {
    if (!editingCell) return;
    const personen = parsePersonen(editValue);
    const existingEntries = entries
      .filter(entry => entry.ID)
      .sort(sortSchichten);
    const editEnd = timeToMinutes(editBis);
    const tage = CAMP_TAGE
      .map(tag => tag.datum)
      .filter(tag => tag === selectedTag || editWiederholungsTage.includes(tag));
    const wiederholungsTage = tage.length > 0 ? tage : [selectedTag];
    const istWiederholung = wiederholungsTage.length > 1;
    const gruppe = istWiederholung
      ? editingCell.wiederholungsGruppe || neueWiederholungsGruppe()
      : null;
    const basePayload = {
      JobName: editingCell.aufgabe,
      UhrzeitVon: minutesToTime(editingCell.slot),
      UhrzeitBis: editEnd !== null && editEnd > editingCell.slot ? editBis : minutesToTime(editingCell.slot + SLOT_STEP),
      Regelmaessig: istWiederholung,
      WiederholungsGruppe: gruppe,
    };

    const eintraege = personen.flatMap(person => wiederholungsTage.map(tag => ({
      ...basePayload,
      Tag: tag,
      Person: person,
    })));

    const res = await fetch(`${host_url}/logistik/schicht/wiederholung`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        Gruppe: editingCell.wiederholungsGruppe || null,
        AlteIds: editingCell.alteIds?.length ? editingCell.alteIds : existingEntries.map(entry => entry.ID),
        Eintraege: eintraege,
      }),
    });
    if (!res.ok) throw new Error(await res.text());

    cancelEdit();
    ladeSchichten();
  }

  async function deleteEntry(id, shouldConfirm = true, gruppe = null) {
    if (shouldConfirm && !window.confirm('Eintrag löschen?')) return;
    if (gruppe) {
      const res = await fetch(`${host_url}/logistik/schicht/wiederholung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Gruppe: gruppe, Eintraege: [] }),
      });
      if (!res.ok) throw new Error(await res.text());
      cancelEdit();
      ladeSchichten();
      return;
    }
    const res = await fetch(`${host_url}/logistik/schicht/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await res.text());
    cancelEdit();
    ladeSchichten();
  }

  function startDienstEdit() {
    cancelEdit();
    setDienstDrafts(aufgaben.map((aufgabe, index) => ({
      draftKey: aufgabe.id || `custom-${taskKey(aufgabe.name)}-${index}`,
      id: aufgabe.id,
      name: aufgabe.name,
      originalName: aufgabe.originalName || aufgabe.name,
      beschreibung: aufgabe.beschreibung || '',
      originalBeschreibung: aufgabe.beschreibung || '',
      soll: aufgabe.soll || '',
      originalSoll: aufgabe.soll || '',
      reihenfolge: index + 1,
      originalReihenfolge: index + 1,
      tag: aufgabe.tag || null,
      scopeKey: aufgabe.scopeKey || 'ALL',
      alleTage: !aufgabe.tag && (aufgabe.scopeKey || 'ALL') === 'ALL',
      custom: aufgabe.custom,
      geloescht: false,
    })));
    setDienstEditMode(true);
  }

  function cancelDienstEdit() {
    setDienstEditMode(false);
    setDienstDrafts([]);
    setDeleteDienstConfirm(null);
  }

  function updateDienstDraft(index, field, value) {
    setDienstDrafts(drafts => drafts.map((draft, i) => (
      i === index ? { ...draft, [field]: value } : draft
    )));
  }

  function addDienstDraft() {
    setDienstDrafts(drafts => [
      ...drafts,
      {
        draftKey: `new-${Date.now()}`,
        id: null,
        name: '',
        originalName: '',
        beschreibung: '',
        originalBeschreibung: '',
        soll: '',
        originalSoll: '',
        reihenfolge: drafts.length + 1,
        originalReihenfolge: drafts.length + 1,
        tag: selectedTag,
        scopeKey: selectedTag,
        alleTage: false,
        custom: false,
        neu: true,
        geloescht: false,
      },
    ]);
  }

  function requestRemoveDienstDraft(index) {
    const draft = dienstDrafts[index];
    if (!draft) return;
    setDeleteDienstConfirm({ index, draft });
  }

  function removeDienstDraft(index) {
    setDienstDrafts(drafts => drafts
      .map((draft, i) => {
        if (i !== index) return draft;
        return draft.neu ? null : { ...draft, geloescht: true };
      })
      .filter(Boolean));
  }

  function confirmRemoveDienstDraft() {
    if (!deleteDienstConfirm) return;
    removeDienstDraft(deleteDienstConfirm.index);
    setDeleteDienstConfirm(null);
  }

  function dienstDraftChanged(draft, index) {
    return Boolean(draft.neu) ||
      draft.geloescht ||
      draft.name.trim() !== String(draft.originalName || '').trim() ||
      String(draft.beschreibung || '').trim() !== String(draft.originalBeschreibung || '').trim() ||
      String(draft.soll || '').trim() !== String(draft.originalSoll || '').trim() ||
      index + 1 !== draft.originalReihenfolge ||
      draft.alleTage !== (!draft.tag && (draft.scopeKey || 'ALL') === 'ALL');
  }

  async function saveDienstDrafts() {
    const aktiveDrafts = dienstDrafts.filter(draft => !draft.geloescht && draft.name.trim());
    const geloeschteAlleTageDrafts = dienstDrafts.filter(draft => draft.geloescht && draft.alleTage);
    const geloeschteTagDrafts = dienstDrafts.filter(draft => draft.geloescht && !draft.alleTage);
    const globaleGeaendert = dienstDrafts.some((draft, index) => draft.alleTage && dienstDraftChanged(draft, index));
    const hatTagesspezifischeDienste = dienste.some(dienst => dienst.tag || (dienst.scopeKey && dienst.scopeKey !== 'ALL'));
    const tagesspezifischGeaendert = dienstDrafts.some((draft, index) => !draft.alleTage && dienstDraftChanged(draft, index));
    const tagKopieSpeichern = tagesspezifischGeaendert || (hatTagesspezifischeDienste && globaleGeaendert);

    for (const draft of geloeschteAlleTageDrafts) {
      const res = await fetch(`${host_url}/logistik/dienste/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Name: draft.originalName || draft.name,
          FuerAlleTage: true,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
    }

    for (const draft of geloeschteTagDrafts) {
      const res = await fetch(`${host_url}/logistik/dienste/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Name: draft.originalName || draft.name,
          Tag: selectedTag,
          FuerAlleTage: false,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
    }

    for (let i = 0; i < aktiveDrafts.length; i += 1) {
      const draft = aktiveDrafts[i];
      if (!draft.alleTage || !dienstDraftChanged(draft, i)) continue;
      const payload = {
        Name: draft.name.trim(),
        Beschreibung: draft.beschreibung?.trim() || null,
        Soll: draft.soll?.trim() || null,
        Reihenfolge: i + 1,
        Aktiv: true,
        AlterName: draft.originalName,
        FuerAlleTage: true,
      };
      const idIstGlobal = draft.id && (draft.scopeKey || 'ALL') === 'ALL';
      const url = idIstGlobal
        ? `${host_url}/logistik/dienste/${draft.id}`
        : `${host_url}/logistik/dienste`;
      const method = idIstGlobal ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
    }

    if (tagKopieSpeichern) {
      const tagDienste = aktiveDrafts.map((draft, index) => ({
        Name: draft.name.trim(),
        Beschreibung: draft.beschreibung?.trim() || null,
        Soll: draft.soll?.trim() || null,
        Reihenfolge: index + 1,
      }));
      const umbenennungen = dienstDrafts
        .filter(draft => !draft.alleTage && !draft.geloescht && draft.originalName && draft.name.trim() && draft.originalName !== draft.name.trim())
        .map(draft => ({ AlterName: draft.originalName, Name: draft.name.trim() }));
      const res = await fetch(`${host_url}/logistik/dienste/tag/${selectedTag}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dienste: tagDienste, umbenennungen }),
      });
      if (!res.ok) throw new Error(await res.text());
    }

    cancelDienstEdit();
    ladeDienste(selectedTag);
    ladeSchichten();
  }

  function beginResize(event, aufgabe, entry) {
    if (!entry?.ID || dienstEditMode) return;
    event.preventDefault();
    event.stopPropagation();
    const startSlot = timeToMinutes(entry.UhrzeitVon);
    if (startSlot === null) return;
    const rawEnd = timeToMinutes(entry.UhrzeitBis);
    const endSlot = rawEnd !== null && rawEnd > startSlot ? rawEnd : startSlot + SLOT_STEP;
    const next = {
      aufgabe: aufgabe.name,
      entry: { ...entry },
      startSlot,
      endSlot,
    };
    resizingRef.current = next;
    setResizingCell(next);
  }

  function updateResize(aufgabe, slot) {
    setResizingCell(current => {
      if (!current || taskKey(current.aufgabe) !== taskKey(aufgabe.name)) return current;
      const next = {
        ...current,
        endSlot: Math.max(current.startSlot + SLOT_STEP, slot + SLOT_STEP),
      };
      resizingRef.current = next;
      return next;
    });
  }

  const finishResize = useCallback(async () => {
    const current = resizingRef.current;
    if (!current) return;
    resizingRef.current = null;
    setResizingCell(null);

    const gruppe = wiederholungsGruppe(current.entry);
    if (gruppe) {
      const gruppenEintraege = alleSchichten.filter(schicht => wiederholungsGruppe(schicht) === gruppe);
      const eintraege = gruppenEintraege.map(entry => ({
        Tag: tagAlsString(entry.Tag),
        JobName: entry.JobName,
        Person: entry.Person,
        UhrzeitVon: minutesToTime(current.startSlot),
        UhrzeitBis: minutesToTime(current.endSlot),
        Regelmaessig: true,
        WiederholungsGruppe: gruppe,
      }));
      const res = await fetch(`${host_url}/logistik/schicht/wiederholung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Gruppe: gruppe, Eintraege: eintraege }),
      });
      if (!res.ok) throw new Error(await res.text());
      ladeSchichten();
      return;
    }

    const payload = {
      Tag: tagAlsString(current.entry.Tag) || selectedTag,
      JobName: current.entry.JobName,
      Person: current.entry.Person,
      UhrzeitVon: minutesToTime(current.startSlot),
      UhrzeitBis: minutesToTime(current.endSlot),
      Regelmaessig: Boolean(current.entry.Regelmaessig),
      WiederholungsGruppe: null,
    };
    const res = await fetch(`${host_url}/logistik/schicht/${current.entry.ID}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await res.text());
    ladeSchichten();
  }, [alleSchichten, ladeSchichten, selectedTag]);

  useEffect(() => {
    if (!resizingCell) return undefined;
    const handlePointerUp = () => {
      finishResize().catch(err => console.error(err));
    };
    window.addEventListener('pointerup', handlePointerUp);
    return () => window.removeEventListener('pointerup', handlePointerUp);
  }, [finishResize, resizingCell]);

  function baueDruckHtml() {
    const headerHtml = matrixAufgaben.map((aufgabe, index) => `
      <th class="print-task-header" key="${escapeHtml(aufgabe.id || aufgabe.draftKey || `${aufgabe.name}-${index}`)}">
        <span class="task-name">${escapeHtml(aufgabe.name)}</span>
        ${aufgabe.soll ? `<span class="task-soll">${escapeHtml(aufgabe.soll)}</span>` : ''}
        ${aufgabe.beschreibung ? `<span class="task-description">${escapeHtml(aufgabe.beschreibung)}</span>` : ''}
      </th>
    `).join('');

    const bodyHtml = ZEITRASTER.map(slot => {
      const cells = matrixAufgaben.map((aufgabe, index) => {
        const printCell = getPrintCell(aufgabe, slot);
        if (!printCell) return '';
        const filledClass = printCell.entries.length ? ' filled' : '';
        const regularClass = printCell.entries.some(entry => entry.Regelmaessig) ? ' regular' : '';
        const rowSpan = printCell.rowSpan > 1 ? ` rowspan="${printCell.rowSpan}"` : '';
        const personen = printCell.entries.map(entry => entry.Person).filter(Boolean).join('\n') || '';
        const zeiten = printCell.entries.map(schichtZeitraum).filter(Boolean).join('\n');
        return `
          <td class="print-matrix-cell${filledClass}${regularClass}"${rowSpan}>
            ${printCell.entries.length > 0 ? `
              <span class="print-cell-person">${htmlMitZeilenumbruechen(personen || '-')}</span>
              <span class="print-cell-time">${htmlMitZeilenumbruechen(zeiten)}</span>
            ` : ''}
          </td>
        `;
      }).join('');
      return `<tr><th class="print-time-cell">${minutesToTime(slot)}</th>${cells}</tr>`;
    }).join('');

    return `<!doctype html>
      <html lang="de">
        <head>
          <meta charset="utf-8" />
          <title>Schichtplan ${escapeHtml(tagLabel)}</title>
          <style>
            @page { size: A4 landscape; margin: 7mm; }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              background: #fff;
              color: #111;
              font-family: Arial, Helvetica, sans-serif;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-day-title {
              margin: 0 0 3.5mm;
              color: #005f68;
              font-size: 12pt;
              font-weight: 700;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              font-size: 5.4pt;
            }
            thead { display: table-header-group; }
            th,
            td {
              border: 0.5pt solid #1c3030;
              padding: 1.1pt 1.5pt;
              vertical-align: top;
              color: #111;
              background: #fff;
            }
            .print-time-header,
            .print-time-cell {
              width: 10mm;
              text-align: center;
              white-space: nowrap;
              font-weight: 700;
            }
            .print-time-cell {
              background: #edf3de;
              font-size: 5.1pt;
            }
            .print-task-header {
              height: 11mm;
              background: #005f68;
              color: #fff;
            }
            .print-task-header span {
              display: block;
              color: #fff;
            }
            .task-name {
              font-size: 5.8pt;
              line-height: 1.02;
              font-weight: 700;
            }
            .task-soll,
            .task-description {
              margin-top: 0.7pt;
              font-size: 4.4pt;
              line-height: 1.02;
            }
            .print-matrix-cell {
              height: 2.35mm;
              min-height: 2.35mm;
            }
            .print-matrix-cell.filled {
              background: #d6edf0;
            }
            .print-matrix-cell.regular {
              box-shadow: inset 1.4pt 0 0 #005f68;
            }
            .print-cell-person,
            .print-cell-time {
              display: block;
              overflow-wrap: anywhere;
            }
            .print-cell-person {
              font-size: 5.2pt;
              font-weight: 700;
              line-height: 1.04;
            }
            .print-cell-time {
              margin-top: 0.6pt;
              color: #004f58;
              font-size: 4.4pt;
              line-height: 1;
            }
          </style>
        </head>
        <body>
          <div class="print-day-title">Schichtplan - ${escapeHtml(tagLabel)}</div>
          <table>
            <thead>
              <tr><th class="print-time-header">Zeit</th>${headerHtml}</tr>
            </thead>
            <tbody>${bodyHtml}</tbody>
          </table>
          <script>
            window.addEventListener('load', () => {
              window.focus();
              setTimeout(() => window.print(), 120);
            });
          </script>
        </body>
      </html>`;
  }

  function openPrintPdf() {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      window.print();
      return;
    }
    printWindow.document.open();
    printWindow.document.write(baueDruckHtml());
    printWindow.document.close();
  }

  return (
    <KernteamLayout>
      <div className="container schichtplan-container">
        <BackArrow to="/logistik" />
        <div className="schichtplan-head no-print">
          <h1 className="section-title teal">Schichtplan</h1>
          {dienstEditMode ? (
            <div className="dienst-editor-actions">
              <button className="logistik-import-btn compact" onClick={addDienstDraft}>
                <span className="material-symbols-outlined">add</span>
                Dienst
              </button>
              <button
                className="logistik-import-btn compact"
                onClick={() => saveDienstDrafts().catch(err => console.error(err))}
              >
                <span className="material-symbols-outlined">check</span>
                Speichern
              </button>
              <button className="logistik-print-link compact" onClick={cancelDienstEdit}>
                <span className="material-symbols-outlined">close</span>
                Abbrechen
              </button>
            </div>
          ) : (
            <button className="logistik-print-link compact" onClick={startDienstEdit}>
              <span className="material-symbols-outlined">edit</span>
              Dienste bearbeiten
            </button>
          )}
        </div>

        <div className="logistik-tabs no-print">
          {CAMP_TAGE.map(t => (
            <button
              key={t.datum}
              className={`logistik-tab${selectedTag === t.datum ? ' active' : ''}`}
              onClick={() => { setSelectedTag(t.datum); cancelEdit(); }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="print-day-title only-print">Schichtplan — {tagLabel}</div>

        <table className="schicht-print-table only-print">
          <thead>
            <tr>
              <th className="print-time-header">Zeit</th>
              {matrixAufgaben.map((aufgabe, index) => (
                <th key={aufgabe.id || aufgabe.draftKey || `${aufgabe.name}-${index}`} className="print-task-header">
                  <span className="task-name">{aufgabe.name}</span>
                  {aufgabe.soll && <span className="task-soll">{aufgabe.soll}</span>}
                  {aufgabe.beschreibung && <span className="task-description">{aufgabe.beschreibung}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ZEITRASTER.map(slot => (
              <tr key={slot}>
                <th className="print-time-cell">{minutesToTime(slot)}</th>
                {matrixAufgaben.map((aufgabe, index) => {
                  const printCell = getPrintCell(aufgabe, slot);
                  if (!printCell) return null;
                  return (
                    <td
                      key={`${aufgabe.id || aufgabe.draftKey || taskKey(aufgabe.name)}-${slot}-${index}`}
                      rowSpan={printCell.rowSpan}
                      className={`print-matrix-cell${printCell.entries.length ? ' filled' : ''}${printCell.entries.some(e => e.Regelmaessig) ? ' regular' : ''}`}
                    >
                      {printCell.entries.length > 0 && (
                        <>
                          <span className="print-cell-person">
                            {printCell.entries.map(entry => entry.Person).filter(Boolean).join('\n') || '–'}
                          </span>
                          <span className="print-cell-time">
                            {printCell.entries.map(schichtZeitraum).filter(Boolean).join('\n')}
                          </span>
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="schicht-matrix-wrapper no-print">
          <table className="schicht-matrix">
            <thead>
              <tr>
                <th className="time-header">Zeit</th>
                {matrixAufgaben.map((aufgabe, index) => (
                  <th key={aufgabe.id || aufgabe.draftKey || `${aufgabe.name}-${index}`} className="task-header">
                    {dienstEditMode ? (
                      <div className="dienst-header-editor">
                        <input
                          className="dienst-title-input"
                          value={dienstDrafts[aufgabe.draftIndex]?.name || ''}
                          placeholder="Dienst"
                          onChange={e => updateDienstDraft(aufgabe.draftIndex, 'name', e.target.value)}
                        />
                        <input
                          className="dienst-soll-input"
                          value={dienstDrafts[aufgabe.draftIndex]?.soll || ''}
                          placeholder="Soll"
                          onChange={e => updateDienstDraft(aufgabe.draftIndex, 'soll', e.target.value)}
                        />
                        <textarea
                          className="dienst-desc-input"
                          value={dienstDrafts[aufgabe.draftIndex]?.beschreibung || ''}
                          placeholder="Beschreibung"
                          rows={2}
                          onChange={e => updateDienstDraft(aufgabe.draftIndex, 'beschreibung', e.target.value)}
                        />
                        <label className="dienst-scope-toggle">
                          <input
                            type="checkbox"
                            checked={Boolean(dienstDrafts[aufgabe.draftIndex]?.alleTage)}
                            onChange={e => updateDienstDraft(aufgabe.draftIndex, 'alleTage', e.target.checked)}
                          />
                          <span>für alle Tage übernehmen</span>
                        </label>
                        <button
                          className="dienst-delete-btn"
                          onClick={() => requestRemoveDienstDraft(aufgabe.draftIndex)}
                          title="Dienst entfernen"
                          aria-label="Dienst entfernen"
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="task-name">{aufgabe.name}</span>
                        {aufgabe.soll && <span className="task-soll">{aufgabe.soll}</span>}
                        {aufgabe.beschreibung && <span className="task-description">{aufgabe.beschreibung}</span>}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ZEITRASTER.map(slot => (
                <tr key={slot}>
                  <th className="time-cell">{minutesToTime(slot)}</th>
                  {matrixAufgaben.map(aufgabe => {
                    let entries = getCellEntries(aufgabe, slot);
                    if (
                      resizingCell &&
                      taskKey(resizingCell.aufgabe) === taskKey(aufgabe.name) &&
                      taskKey(resizingCell.entry.JobName) === taskKey(aufgabe.matchName || aufgabe.name)
                    ) {
                      const previewEntry = {
                        ...resizingCell.entry,
                        UhrzeitVon: minutesToTime(resizingCell.startSlot),
                        UhrzeitBis: minutesToTime(resizingCell.endSlot),
                      };
                      entries = entries.filter(entry => entry.ID !== previewEntry.ID);
                      if (schichtDecktSlot(previewEntry, slot)) entries = [...entries, previewEntry].sort(sortSchichten);
                    }
                    const startingEntries = entries.filter(entry => schichtStartetInSlot(entry, slot));
                    const isContinuation = entries.length > 0 && startingEntries.length === 0;
                    const displayEntries = startingEntries.length > 0 ? startingEntries : entries;
                    const editKey = `${aufgabe.id || aufgabe.draftKey || taskKey(aufgabe.matchName || aufgabe.name)}-${slot}`;
                    const isEditing =
                      editingCell &&
                      taskKey(editingCell.aufgabe) === taskKey(aufgabe.name) &&
                      editingCell.displaySlot === slot;
                    const cellTitle = entries
                      .map(entry => `${entry.Person || 'Unbesetzt'} ${schichtZeitraum(entry)}`.trim())
                      .join('\n');
                    return (
                      <td
                        key={editKey}
                        className={`matrix-cell${entries.length ? ' filled' : ''}${isContinuation ? ' continued' : ''}${entries.some(e => e.Regelmaessig) ? ' regular' : ''}`}
                        onPointerEnter={() => updateResize(aufgabe, slot)}
                      >
                        {isEditing ? (
                          <div className="cell-editor">
                            <textarea
                              className="cell-editor-text"
                              value={editValue}
                              autoFocus
                              rows={3}
                              onChange={e => setEditValue(e.target.value)}
                            />
                            <div className="cell-editor-meta">
                              <label className="cell-time-field">
                                Bis
                                <input
                                  type="time"
                                  value={editBis}
                                  onChange={e => setEditBis(e.target.value)}
                                />
                              </label>
                              <div className="repeat-day-picker" role="group" aria-label="Wiederholen am">
                                <span className="repeat-day-label">Wiederholen am</span>
                                <div className="repeat-day-buttons">
                                  {CAMP_TAGE.map(tag => {
                                    const active = editWiederholungsTage.includes(tag.datum) || tag.datum === selectedTag;
                                    const locked = tag.datum === selectedTag;
                                    return (
                                      <button
                                        key={tag.datum}
                                        type="button"
                                        className={`repeat-day-chip${active ? ' active' : ''}${locked ? ' locked' : ''}`}
                                        onClick={() => toggleWiederholungsTag(tag.datum)}
                                        aria-pressed={active}
                                        title={locked ? 'Aktueller Tag' : 'Tag wiederholen'}
                                      >
                                        {tag.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                            <div className="cell-editor-actions">
                              <button
                                className="matrix-icon-btn save"
                                onClick={() => saveCell(entries).catch(err => console.error(err))}
                                title="Speichern"
                                aria-label="Speichern"
                              >
                                <span className="material-symbols-outlined">check</span>
                              </button>
                              <button
                                className="matrix-icon-btn cancel"
                                onClick={cancelEdit}
                                title="Abbrechen"
                                aria-label="Abbrechen"
                              >
                                <span className="material-symbols-outlined">close</span>
                              </button>
                              {entries[0] && (
                                <button
                                  className="matrix-icon-btn delete"
                                  onClick={() => deleteEntry(entries[0].ID, true, wiederholungsGruppe(entries[0])).catch(err => console.error(err))}
                                  title="Löschen"
                                  aria-label="Löschen"
                                >
                                  <span className="material-symbols-outlined">delete</span>
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <button
                            className="matrix-cell-button"
                            onClick={() => beginEdit(aufgabe, slot, entries)}
                            disabled={dienstEditMode}
                            title={cellTitle}
                          >
                            {entries.length > 0 ? (
                              isContinuation ? (
                                <span className="cell-continuation" aria-hidden="true"></span>
                              ) : (
                                <>
                                  <span className="cell-person">
                                    {displayEntries.map(e => e.Person).filter(Boolean).join('\n') || '–'}
                                  </span>
                                  <span className="cell-time-range">
                                    {displayEntries.map(schichtZeitraum).filter(Boolean).join('\n')}
                                  </span>
                                  {entries.some(e => e.Regelmaessig) && (
                                    <span className="cell-regular" title="Regelmäßig">↻</span>
                                  )}
                                  {displayEntries[0]?.ID && !dienstEditMode && (
                                    <span
                                      className="cell-resize-handle no-print"
                                      title="Dauer ziehen"
                                      onClick={e => e.stopPropagation()}
                                      onPointerDown={e => beginResize(e, aufgabe, displayEntries[0])}
                                    ></span>
                                  )}
                                </>
                              )
                            ) : (
                              <span className="cell-empty" aria-hidden="true">+</span>
                            )}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="logistik-footer no-print">
          <button className="logistik-import-btn" onClick={() => setShowImport(true)}>
            <span className="material-symbols-outlined">upload</span>
            Excel importieren
          </button>
          <button className="logistik-print-link" onClick={openPrintPdf}>
            <span className="material-symbols-outlined">print</span>
            <span className="print-text">PDF/Drucken</span>
          </button>
        </div>

        {deleteDienstConfirm && (
          <div className="dienst-confirm-overlay no-print" role="presentation">
            <div className="dienst-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="dienst-delete-title">
              <h2 id="dienst-delete-title">Dienst löschen?</h2>
              <p>
                {deleteDienstConfirm.draft?.alleTage
                  ? 'Dieser Dienst wird an allen Tagen aus dem Schichtplan entfernt.'
                  : `Dieser Dienst wird nur am ${tagLabel} aus dem Schichtplan entfernt.`}
              </p>
              <div className="dienst-confirm-buttons">
                <button className="dienst-confirm-no" onClick={() => setDeleteDienstConfirm(null)}>
                  Nein, weiter
                </button>
                <button className="dienst-confirm-yes" onClick={confirmRemoveDienstDraft}>
                  Ja, löschen
                </button>
              </div>
            </div>
          </div>
        )}

        {showImport && (
          <ImportModal
            title="Schichtplan importieren"
            columns={IMPORT_COLUMNS}
            templateName="vorlage_schichtplan"
            templateRows={[]}
            buildTemplateWorkbook={XLSX => baueSchichtplanVorlage(XLSX, matrixAufgaben.filter(aufgabe => !aufgabe.custom))}
            parseWorkbook={parseSchichtplanMatrix}
            previewColumns={IMPORT_COLUMNS}
            onImport={async (rows, ersetzen, meta) => {
              const tage = [...new Set(rows.map(r => r.Tag).filter(Boolean))];
              const res = await fetch(`${host_url}/logistik/schicht/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  eintraege: rows,
                  ersetzen,
                  tage,
                  dienste: meta?.dienste || [],
                }),
              });
              if (!res.ok) throw new Error(await res.text());
              ladeDienste(selectedTag);
              ladeSchichten();
            }}
            onClose={() => setShowImport(false)}
          />
        )}
      </div>
    </KernteamLayout>
  );
}

export default Schichtplan;
